import {
  InspectCollectionInput,
  MigrationIndexChange,
  MigrationSample,
  MigrationSpec,
  MigrationState,
  MigrationStatus,
  ProbeFetcher,
  isSeedSpec
} from "./migration.models"
import { diffDocument } from "./migration.diff"

/**
 * All of the "is this migration done?" reasoning lives here, in the browser. The API only counts
 * documents and lists indexes; deciding what those numbers MEAN is this file's job, which is what
 * lets a new migration be a registry entry rather than a backend change.
 */

const SAMPLE_LIMIT = 5

type ProbeCount = NonNullable<InspectCollectionInput["counts"]>[number]

/**
 * The one count that measures a spec's progress — what a run is judged by, before and after. An
 * update spec counts what is still `affected`; a seed counts what is already `present`, because a
 * document that does not exist yet cannot be counted.
 */
export function progressCount(spec: MigrationSpec): ProbeCount {
  return isSeedSpec(spec)
    ? { key: "present", filter: spec.expectedDocuments.filter }
    : { key: "affected", filter: spec.affectedFilter }
}

/** Mongo reports index keys as an object; compare them order-insensitively by name and direction. */
function keysMatch(actual: string, expected: Record<string, 1 | -1>): boolean {
  try {
    const parsed = JSON.parse(actual) as Record<string, number>
    const a = Object.entries(parsed)
    const b = Object.entries(expected)
    if (a.length !== b.length) return false
    return b.every(([key, dir]) => parsed[key] === dir) && a.length === b.length
  } catch {
    return false
  }
}

function describeKeys(keys: Record<string, 1 | -1>): string {
  return `{ ${Object.entries(keys)
    .map(([k, v]) => `${k}: ${v}`)
    .join(", ")} }`
}

function buildIndexChanges(
  spec: MigrationSpec,
  actual: { name: string; keys: string; unique: boolean }[]
): MigrationIndexChange[] {
  const changes: MigrationIndexChange[] = []

  for (const expected of spec.indexes?.mustDrop ?? []) {
    const found = actual.find(
      (index) =>
        keysMatch(index.keys, expected.keys) &&
        (expected.unique === undefined || index.unique === expected.unique)
    )
    if (found) {
      changes.push({
        name: found.name,
        keys: describeKeys(expected.keys),
        unique: found.unique,
        action: "DROP",
        present: true
      })
    }
  }

  for (const expected of spec.indexes?.mustExist ?? []) {
    const found = actual.find((index) => keysMatch(index.keys, expected.keys))
    changes.push({
      name: found?.name ?? describeKeys(expected.keys),
      keys: describeKeys(expected.keys),
      unique: expected.unique ?? found?.unique ?? false,
      action: found ? "KEEP" : "CREATE",
      present: !!found
    })
  }

  return changes
}

/**
 * Index work counts as outstanding work even when every document is already migrated. Without
 * that, a half-finished run reads as APPLIED while the tenant still hits E11000 on the index that
 * never got dropped.
 */
function resolveStatus(
  affected: number,
  changes: MigrationIndexChange[],
  total: number,
  tracksIndexes: boolean
): MigrationStatus {
  const documentWork = affected > 0
  const indexWork = changes.some((change) => change.action !== "KEEP")
  if (!documentWork && !indexWork) return "APPLIED"
  // PARTIAL is the two halves disagreeing. A migration that declares no index has only one half,
  // so everything still outstanding there is simply PENDING.
  if (tracksIndexes && documentWork !== indexWork && total > 0) return "PARTIAL"
  return "PENDING"
}

function buildSamples(spec: MigrationSpec, raw: string[]): MigrationSample[] {
  return raw.map((json) => {
    const doc = JSON.parse(json) as Record<string, unknown>
    const id = String(doc._id ?? "")
    // The sample renders the payload, not its identity — _id is shown separately.
    const rest = { ...doc }
    delete rest._id
    const after = spec.preview ? spec.preview(rest) : rest
    return {
      id,
      before: JSON.stringify(rest, null, 2),
      after: JSON.stringify(after, null, 2),
      changes: diffDocument(rest, after)
    }
  })
}

/** Probes one spec and turns the raw numbers into a state the page can render. */
export async function probeMigration(
  spec: MigrationSpec,
  fetchProbe: ProbeFetcher
): Promise<MigrationState> {
  const empty: MigrationState = {
    spec,
    status: "UNKNOWN",
    affectedCount: 0,
    totalCount: 0,
    indexChanges: [],
    samples: []
  }

  try {
    const seed = isSeedSpec(spec)
    const result = await fetchProbe({
      collection: spec.collection,
      counts: [progressCount(spec)],
      estimatedTotal: true,
      withIndexes: !!spec.indexes,
      ...(seed
        ? {}
        : {
            sample: {
              filter: spec.affectedFilter,
              fields: ["_id", ...spec.sampleFields],
              limit: SAMPLE_LIMIT
            }
          })
    })

    const counts = Object.fromEntries(
      result.counts.map((c) => [c.key, c.count])
    )
    const indexChanges = spec.indexes
      ? buildIndexChanges(spec, result.indexes)
      : []
    const totalCount = result.estimatedTotal ?? 0
    const presentCount = seed ? (counts.present ?? 0) : undefined
    const affectedCount = seed
      ? Math.max(0, spec.expectedDocuments.count - (presentCount ?? 0))
      : (counts.affected ?? 0)

    return {
      spec,
      status: resolveStatus(
        affectedCount,
        indexChanges,
        totalCount,
        !!spec.indexes
      ),
      affectedCount,
      totalCount,
      presentCount,
      indexChanges,
      samples: seed ? [] : buildSamples(spec, result.samples)
    }
  } catch (error) {
    // One bad probe degrades its own row — an operator who cannot open the page cannot fix
    // anything either.
    return {
      ...empty,
      probeError: error instanceof Error ? error.message : String(error)
    }
  }
}
